import React from 'react';

import { postWebUiApi } from '../../api';
import { WebUiAlert } from '../../components/alert';
import { WebUiButton } from '../../components/button';
import { WebUiInput } from '../../components/input';
import WebUiPageWrapper from '../../components/page-wrapper';
import { useWebUiGlobalContext } from '../../context/global/use-global-context';

type MqttConfig = Record<string, unknown> & {
  enabled?: boolean;
  server?: string;
  port?: number;
  name?: string;
  username?: string;
  password?: string;
  tls?: boolean;
  verify?: boolean;
  qos?: number;
  update_enabled?: boolean;
  update_topic?: string;
  pingreq_interval?: number;
  s?: string;
  ca_cert?: string;
};

export default function WebUiSettingsMqttPage() {
  const { currentUser } = useWebUiGlobalContext();
  const readonly = currentUser?.userLevel === 1;
  const [cfg, setCfg] = React.useState<MqttConfig>({});
  const [caFile, setCaFile] = React.useState<File | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [message, setMessage] = React.useState<string | null>(null);

  const reload = React.useCallback(async () => {
    const data = await postWebUiApi<MqttConfig>({
      service: 'mqtt',
      access: 'get',
      command: 'get mqtt',
    });
    setCfg(data ?? {});
  }, []);

  React.useEffect(() => {
    reload().catch((err) => setError((err as Error).message));
  }, [reload]);

  const setField = <K extends keyof MqttConfig>(k: K, v: MqttConfig[K]) =>
    setCfg((p) => ({ ...p, [k]: v }));

  return (
    <WebUiPageWrapper titleList={['Settings', 'MQTT']}>
      {message ? <WebUiAlert type="success" message={message} /> : null}
      {error ? <WebUiAlert type="error" message={error} /> : null}
      <div className="flex w-full flex-row gap-2">
        <div className="flex w-1/3 flex-col gap-2">
          <h4 className="font-bold">General Configuration</h4>
          <table className="webui-table w-full">
            <thead>
              <tr className="webui-table-row-head">
                <th>Setting</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              <tr className="webui-table-row-content">
                <td>Enable</td>
                <td>
                  <WebUiInput
                    inputSize="sm"
                    type="checkbox"
                    disabled={readonly}
                    checked={Boolean(cfg.enabled)}
                    onChange={(e) => setField('enabled', e.target.checked)}
                  />
                </td>
              </tr>
              <tr className="webui-table-row-content">
                <td>Broker hostname</td>
                <td>
                  <WebUiInput
                    inputSize="sm"
                    disabled={readonly || !cfg.enabled}
                    value={String(cfg.server ?? '')}
                    onChange={(e) => setField('server', e.target.value)}
                  />
                </td>
              </tr>
              <tr className="webui-table-row-content">
                <td>Broker port</td>
                <td>
                  <WebUiInput
                    inputSize="sm"
                    type="number"
                    disabled={readonly || !cfg.enabled}
                    value={Number(cfg.port ?? 1883)}
                    onChange={(e) => setField('port', Number(e.target.value))}
                  />
                </td>
              </tr>
              <tr className="webui-table-row-content">
                <td>Client ID</td>
                <td>
                  <WebUiInput
                    inputSize="sm"
                    className="webui-input w-full"
                    disabled={readonly || !cfg.enabled}
                    value={String(cfg.name ?? '')}
                    onChange={(e) => setField('name', e.target.value)}
                  />
                </td>
              </tr>
              <tr className="webui-table-row-content">
                <td>Username</td>
                <td>
                  <WebUiInput
                    inputSize="sm"
                    disabled={readonly || !cfg.enabled}
                    value={String(cfg.username ?? '')}
                    onChange={(e) => setField('username', e.target.value)}
                  />
                </td>
              </tr>
              <tr className="webui-table-row-content">
                <td>Password</td>
                <td>
                  <WebUiInput
                    inputSize="sm"
                    type="password"
                    disabled={readonly || !cfg.enabled}
                    value={String(cfg.password ?? '')}
                    onChange={(e) => setField('password', e.target.value)}
                  />
                </td>
              </tr>
              <tr className="webui-table-row-content">
                <td>TLS enabled</td>
                <td>
                  <WebUiInput
                    inputSize="sm"
                    type="checkbox"
                    disabled={readonly || !cfg.enabled}
                    checked={Boolean(cfg.tls)}
                    onChange={(e) => setField('tls', e.target.checked)}
                  />
                </td>
              </tr>
              <tr className="webui-table-row-content">
                <td>CA cert</td>
                <td>
                  <WebUiInput
                    inputSize="sm"
                    type="file"
                    disabled={readonly || !cfg.enabled || !cfg.tls}
                    onChange={(e) => setCaFile(e.target.files?.[0] ?? null)}
                  />
                </td>
              </tr>
              <tr className="webui-table-row-content">
                <td>Verify</td>
                <td>
                  <WebUiInput
                    inputSize="sm"
                    type="checkbox"
                    disabled={readonly || !cfg.enabled || !cfg.tls}
                    checked={Boolean(cfg.verify)}
                    onChange={(e) => setField('verify', e.target.checked)}
                  />
                </td>
              </tr>
              <tr className="webui-table-row-content">
                <td>QoS</td>
                <td>
                  <WebUiInput
                    inputSize="sm"
                    type="number"
                    min={0}
                    max={2}
                    disabled={readonly || !cfg.enabled}
                    value={Number(cfg.qos ?? 0)}
                    onChange={(e) => setField('qos', Number(e.target.value))}
                  />
                </td>
              </tr>
              <tr className="webui-table-row-content">
                <td>Keep alive interval (seconds)</td>
                <td>
                  <WebUiInput
                    inputSize="sm"
                    type="number"
                    disabled={readonly || !cfg.enabled}
                    value={Number(cfg.s ?? 0)}
                    onChange={(e) => setField('s', String(e.target.value))}
                  />
                </td>
              </tr>
            </tbody>
          </table>
          <div>
            <WebUiButton
              disabled={readonly}
              onClick={async () => {
                try {
                  let caText = cfg.ca_cert ? String(cfg.ca_cert) : '';
                  if (caFile) caText = await caFile.text();
                  await postWebUiApi({
                    service: 'mqtt',
                    access: 'set',
                    command: 'set mqtt',
                    parameters: { ...cfg, ca_cert: caText },
                  });
                  setMessage('MQTT settings updated.');
                  setError(null);
                  await reload();
                } catch (err) {
                  setError((err as Error).message);
                }
              }}
            >
              Set MQTT Configuration
            </WebUiButton>
          </div>
        </div>
        <div className="flex w-1/3 flex-col gap-2">
          <h4 className="font-bold">Update</h4>
          <table className="webui-table w-full">
            <thead>
              <tr className="webui-table-row-head">
                <th>Setting</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              <tr className="webui-table-row-content tooltip">
                <td>Update subscribe</td>
                <td>
                  <WebUiInput type="checkbox" disabled />
                </td>
              </tr>
              <tr className="webui-table-row-content">
                <td>Update topic</td>
                <td>
                  <WebUiInput
                    inputSize="sm"
                    disabled={readonly || !cfg.enabled}
                    value={String(cfg.update_topic ?? '')}
                    onChange={(e) => setField('update_topic', e.target.value)}
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="flex w-1/3 flex-col gap-2">
          <h4 className="font-bold">Status</h4>
          <div className="flex w-full flex-col gap-2">
            <table className="webui-table w-full">
              <thead>
                <tr className="webui-table-row-head">
                  <th>System</th>
                  <th>Value</th>
                </tr>
              </thead>
              <tbody>
                <tr className="webui-table-row-content">
                  <td>Status </td>
                  <td>connected</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </WebUiPageWrapper>
  );
}
